import {Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {DateRange} from '../../../types/date-range';

import {TranslatePipe} from '@ngx-translate/core';

@Component({
  selector: 'app-date-range-selector',
  imports: [
    FormsModule,
    TranslatePipe
],
  templateUrl: './date-range-selector.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './date-range-selector.component.scss'
})
export class DateRangeSelectorComponent implements OnInit {

  @Input('date') dateSelector: string = 'this-month';
  @Input('timezone') timezone: string = '';
  @Input('shortTimezone') shortTimezone: boolean = false;
  @Input('showTimezone') showTimezone: boolean = true;
  @Input() initialFrom: string = '';
  @Input() initialTo: string = '';
  @Output() dateChanged = new EventEmitter<DateRange>();
  @Output() dateRangeInvalidChange = new EventEmitter<boolean>();

  dateFrom: Date = new Date();
  dateTo: Date = new Date();

  dateFromSelector: string = '';
  dateToSelector: string = '';

  dateRangeInvalid: boolean = false;

  ngOnInit() {
    if (!this.timezone) {
      this.timezone = Math.round(-(new Date(2000, 0, 1)).getTimezoneOffset() / 60).toString();
    }

    if (this.dateSelector === 'custom') {
      if (this.initialFrom && this.initialTo) {
        this.dateFromSelector = this.initialFrom;
        this.dateToSelector = this.initialTo;
      }

      if (!this.dateFromSelector || !this.dateToSelector) {
        this.dateSelector = 'this-month';
      }
    }

    this.onDateChange();
  }

  hasDst(): boolean {
    let jan = (new Date(2000, 0, 1)).getTimezoneOffset();
    let jul = (new Date(2000, 6, 1)).getTimezoneOffset();
    return jan != jul;
  }

  getTimezones(): number[] {
    return Array.from({length: 27}, (_, i) => i - 12);
  }

  computeDate(): void {
    let dateFrom: Date = new Date(this.dateFromSelector);
    let dateTo: Date = new Date(this.dateToSelector);
    const now = new Date();

    switch (this.dateSelector) {
      case 'today':
        dateFrom = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
        dateTo = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999));
        break;
      case 'yesterday':
        dateFrom = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate() - 1));
        dateTo = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate() - 1, 23, 59, 59, 999));
        break;
      case 'this-week':
        dateFrom = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay() + 1));
        dateTo = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999));
        break;
      case 'last-week':
        dateFrom = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay() - 6));
        dateTo = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay(), 23, 59, 59, 999));
        break;
      case 'this-month':
        dateFrom = new Date(Date.UTC(now.getFullYear(), now.getMonth(), 1));
        dateTo = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999));
        break;
      case 'last-month':
        const dayCount = new Date(now.getFullYear(), now.getMonth(), 0).getDate();
        dateFrom = new Date(Date.UTC(now.getFullYear(), now.getMonth() - 1, 1));
        dateTo = new Date(Date.UTC(now.getFullYear(), now.getMonth() - 1, dayCount, 23, 59, 59, 999));
        break;
      case 'this-year':
        dateFrom = new Date(Date.UTC(now.getFullYear(), 0, 1));
        dateTo = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999));
        break;
      case 'last-year':
        dateFrom = new Date(Date.UTC(now.getFullYear() - 1, 0, 1));
        dateTo = new Date(Date.UTC(now.getFullYear() - 1, 11, 31, 23, 59, 59, 999));
        break;
    }

    const negativeTimezone = Number(this.timezone) < 0;
    const realTimezone = Number(this.timezone) + (this.hasDst() ? 1 : 0);
    const timezone = (negativeTimezone ? '-' : '+') + Math.abs(Number(realTimezone)).toString().padStart(2, '0') + ':00';

    this.dateFromSelector = dateFrom.toISOString().split('T')[0];
    this.dateToSelector = dateTo.toISOString().split('T')[0];

    this.dateFrom = new Date(dateFrom.toISOString().split('Z')[0] + timezone);
    this.dateTo = new Date(dateTo.toISOString().split('Z')[0] + timezone);
  }

  onDateChange(): void {
    this.computeDate();

    this.dateRangeInvalid = this.dateTo < this.dateFrom;
    this.dateRangeInvalidChange.emit(this.dateRangeInvalid);

    if (!this.dateRangeInvalid) {
      this.dateChanged.emit({
        start: this.dateFrom,
        end: this.dateTo,
      });
    }
  }

  isCustomDate(): boolean {
    return this.dateSelector === 'custom';
  }

  getDateFrom(): Date {
    return this.dateFrom;
  }

  getDateTo(): Date {
    return this.dateTo;
  }
}
